if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      vmmvAddToCartUnsubscriber = undefined;

      constructor() {
        super();
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (this.submitButton) {
          this.submitButtonText = this.submitButton.querySelector('span');
        }

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.pdpMainProductForm = this.closest('.js-pdp-po-main-buy-buttons')?.querySelector('product-form');

        if (this.pdpMainProductForm === this && !this.vmmvAddToCartUnsubscriber) {
          this.vmmvAddToCartUnsubscriber = subscribe(PUB_SUB_EVENTS.VMMVAddToCart, function(eventData) {
            // Pass the variantId from the event data, or null to use the default
            // Pass the externalTrigger option to true useful if the submit button isn't rendered so skip the UI controls (out of stock products)
            this.onSubmitHandler(new Event('submit', { bubbles: true, cancelable: true }), eventData?.productVariantId || null, { externalTrigger: true });
          }.bind(this));
        }
      }

      disconnectedCallback() {
        this.vmmvAddToCartUnsubscriber?.();
        this.vmmvAddToCartUnsubscriber = undefined;
      }

      getTealiumTrackingContext(evt, customVariantId = null) {
        const submitter = evt?.submitter || this.submitButton || this.form?.querySelector('[type="submit"][name="add"]');
        const dataset = submitter?.dataset || {};
        let productData = null;

        if (dataset.trackingProduct) {
          try {
            productData = JSON.parse(dataset.trackingProduct);
          } catch (_error) {
            productData = null;
          }
        }

        return {
          productHandle: dataset.handle || '',
          variantSku: dataset.sku || dataset.description || '',
          productId: dataset.trackingProductId || '',
          variantId: customVariantId || dataset.variantId || this.variantIdInput?.value || '',
          productData
        };
      }

      getAlgoliaTrackingContext(evt, customVariantId = null) {
        const trackingContext = this.getTealiumTrackingContext(evt, customVariantId);
        const fallbackItem = {
          objectID: String(
            customVariantId
            || this.variantIdInput?.value
            || trackingContext.variantId
            || trackingContext.productId
            || ''
          ).trim(),
          handle: trackingContext.productHandle,
          productId: trackingContext.productId,
          quantity: 1,
          price: Number.isFinite(Number(trackingContext?.productData?.Price))
            ? Number(trackingContext.productData.Price)
            : null,
        };

        if (typeof window.poAlgoliaInsights?.getItemFromElement !== 'function') {
          return fallbackItem;
        }

        return window.poAlgoliaInsights.getItemFromElement(
          evt?.submitter || this.submitButton || this.form || this,
          fallbackItem
        );
      }

      buildLineItemPropertiesFromForm() {
        const properties = {};
        const formData = new FormData(this.form);

        formData.forEach((value, key) => {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (!propertyMatch) return;

          properties[propertyMatch[1]] = value;
        });

        return properties;
      }

      onSubmitHandler(evt, customVariantId = null, options = {}) {
        evt.preventDefault();
        
        // Useful if the submit button isn't rendered so skip the UI controls (out of stock products)
        const isExternalTrigger = options.externalTrigger === true;

        // Update the value of the existing hidden input with a precise timestamp
        const timestampInput = this.form.querySelector('[name="properties[_added_at]"]');
        
        if (timestampInput) {
          timestampInput.value = new Date().toISOString(); // Millisecond precision
        }

        if (!isExternalTrigger) {
          if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        }

        this.handleErrorMessage();

        if (!isExternalTrigger) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner').classList.remove('hidden');
        }

        // Check if nestedCartLines are needed
        let nestedLinesInputs = this.querySelectorAll('.js-nested-cart-item');

        let config;
        let formData;

        // Use customVariantId if provided, otherwise fall back to the input value
        const variantIdToUse = customVariantId || this.variantIdInput.value;

        // If nested cart lines are not needed, the standard Dawn fetch is used.
        // Otherwise a json is created and the fetch type is changed
        if (nestedLinesInputs.length == 0) {
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          formData = new FormData(this.form);
          formData.set('id', variantIdToUse); // override id if customVariantId is provided

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;

        } else {
          const items = [];
          const lineItemProperties = this.buildLineItemPropertiesFromForm();

          items.push({
            id: variantIdToUse,
            quantity: 1,
            properties: lineItemProperties
          });


          nestedLinesInputs.forEach(input => {
            if (input.name == 'monogram-input') {
              if (input.dataset.monogramVariantId != '' && input.dataset.monogramText != '') {
                this.handleMonogramNestedProduct(variantIdToUse, items, input);
              }
            }
          });

          formData = {
            items,
            sections: this.cart?.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname
          };

          if (this.cart) {
            this.cart.setActiveElement(document.activeElement);
          }

          config = fetchConfig("json");
          config.headers["X-Requested-With"] = "XMLHttpRequest";
          config.body = JSON.stringify(formData);
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            const productVariantId = nestedLinesInputs.length > 0 ? formData.items[0].id : formData.get("id");

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: productVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) {
                this.submitButtonText.classList.add('hidden');
              }
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const trackingContext = this.getTealiumTrackingContext(evt, productVariantId);
            const algoliaTrackingContext = this.getAlgoliaTrackingContext(evt, productVariantId);
            const cartUpdatePayload = {
              source: 'product-form',
              productVariantId: productVariantId,
              cartData: response,
              productHandle: trackingContext.productHandle,
              variantSku: trackingContext.variantSku,
              productId: trackingContext.productId,
              variantId: trackingContext.variantId,
              productData: trackingContext.productData
            };

            if (typeof window.poAlgoliaInsights?.trackAddedToCart === 'function') {
              window.poAlgoliaInsights.trackAddedToCart({
                source: algoliaTrackingContext?.source || '',
                items: [algoliaTrackingContext],
              });
            }

            document.dispatchEvent(new CustomEvent('po:cart:item-added', {
              detail: cartUpdatePayload,
            }));

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, cartUpdatePayload).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            
            // Close any parent dialog if have proper data attributes
            if (this.dataset.closeOnAdd == 'true') {
              if (this.dataset.parentDialogId) {
                document.querySelector(`[id='${this.dataset.parentDialogId}']`)?.close();
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!isExternalTrigger) {
              this.submitButton.classList.remove('loading');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = window.variantStrings.addToBag;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      handleMonogramNestedProduct(variantIdToUse,itemArray, input) {

        itemArray.push({
          id: input.dataset.monogramVariantId,
          quantity: 1,
          properties: {
            monogram_text: input.dataset.monogramText,
            monogram_font: input.dataset.monogramFont,
            monogram_parent_id: variantIdToUse
          },
          parent_id: variantIdToUse,
        });
      }
    }
  );
}
